import { useMemo, useState } from 'react'
import {
  useIdeaBoardCategories,
  useCreateCategory,
  useUpdateCategory,
  useDeleteCategory,
  useAdminSuggestions,
} from '@/idea-board/hooks/useIdeaBoardData'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { Plus, Pencil, Trash2, Tags } from 'lucide-react'

export default function CategoryAdminPanel({ canWrite }) {
  const { data: categories = [], isLoading } = useIdeaBoardCategories()
  const { data: suggestions = [] } = useAdminSuggestions(true)
  const createMutation = useCreateCategory()
  const updateMutation = useUpdateCategory()
  const deleteMutation = useDeleteCategory()

  const [addOpen, setAddOpen] = useState(false)
  const [editCategory, setEditCategory] = useState(null)
  const [formName, setFormName] = useState('')
  const [formError, setFormError] = useState('')
  const [deleteError, setDeleteError] = useState('')

  const usageByName = useMemo(() => {
    const map = {}
    suggestions.forEach((s) => {
      map[s.category] = (map[s.category] || 0) + 1
    })
    return map
  }, [suggestions])

  const resetForm = () => {
    setFormName('')
    setFormError('')
  }

  const openAdd = () => {
    resetForm()
    setAddOpen(true)
  }

  const openEdit = (cat) => {
    setFormName(cat.name)
    setFormError('')
    setEditCategory(cat)
  }

  const handleCreate = async (e) => {
    e.preventDefault()
    const name = formName.trim()
    if (!name) {
      setFormError('Name is required')
      return
    }
    try {
      await createMutation.mutateAsync({ name })
      setAddOpen(false)
      resetForm()
    } catch (err) {
      setFormError(err?.message || 'Failed to create category')
    }
  }

  const handleUpdate = async (e) => {
    e.preventDefault()
    const name = formName.trim()
    if (!name || !editCategory) {
      setFormError('Name is required')
      return
    }
    try {
      await updateMutation.mutateAsync({
        id: editCategory.id,
        name,
      })
      setEditCategory(null)
      resetForm()
    } catch (err) {
      setFormError(err?.message || 'Failed to update category')
    }
  }

  const handleDelete = async (id) => {
    setDeleteError('')
    try {
      await deleteMutation.mutateAsync(id)
    } catch (err) {
      setDeleteError(err?.message || 'Failed to delete category')
    }
  }

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Tags className="w-4 h-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold text-foreground">Categories</h3>
          <span className="text-xs text-muted-foreground">
            — synced to board filter and submit form (A–Z)
          </span>
        </div>
        {canWrite && (
          <Button type="button" size="sm" className="gap-1.5 h-8" onClick={openAdd}>
            <Plus className="w-3.5 h-3.5" />
            Add Category
          </Button>
        )}
      </div>

      {deleteError ? (
        <p className="mb-3 text-sm text-destructive" role="alert">
          {deleteError}
        </p>
      ) : null}

      {isLoading ? (
        <Skeleton className="h-32 rounded-xl" />
      ) : (
        <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/60 hover:bg-secondary/60">
                <TableHead className="font-semibold text-xs text-muted-foreground">Name</TableHead>
                <TableHead className="w-24 font-semibold text-xs text-muted-foreground">
                  Ideas
                </TableHead>
                {canWrite && (
                  <TableHead className="w-28 font-semibold text-xs text-muted-foreground">
                    Actions
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories.map((cat) => (
                <TableRow key={cat.id}>
                  <TableCell className="font-medium text-sm">{cat.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {usageByName[cat.name] || 0}
                  </TableCell>
                  {canWrite && (
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => openEdit(cat)}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                              disabled={(usageByName[cat.name] || 0) > 0}
                              title={
                                (usageByName[cat.name] || 0) > 0
                                  ? 'Cannot delete — ideas use this category'
                                  : 'Delete category'
                              }
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete category?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Delete &quot;{cat.name}&quot;? This cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                onClick={() => handleDelete(cat.id)}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {categories.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={canWrite ? 3 : 2}
                    className="text-center py-8 text-muted-foreground text-sm"
                  >
                    No categories yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Category</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g. Process Improvement"
                maxLength={80}
              />
            </div>
            {formError && <p className="text-sm text-destructive">{formError}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Adding...' : 'Add'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editCategory} onOpenChange={(open) => !open && setEditCategory(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Category</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                maxLength={80}
              />
            </div>
            {formError && <p className="text-sm text-destructive">{formError}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditCategory(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
